/**
 * StickyHorizontalScrollbar (Feb 2026)
 *
 * A viewport-pinned horizontal scrollbar that mirrors whichever
 * `.overflow-x-auto` (or `[data-sticky-hscroll]`) element is currently
 * intersecting the viewport AND has its native scrollbar hidden below the
 * fold. Solves the "pan columns without scrolling to the last row" UX pain
 * on wide MES grids (Article Master, Sales Orders, Packing Slips, etc).
 *
 * How it works:
 *   1. Poll on scroll / resize / DOM mutation.
 *   2. Query every `.overflow-x-auto` inside <main>. Skip ones whose content
 *      doesn't overflow horizontally.
 *   3. Pick the one whose top is above the viewport bottom AND whose native
 *      scrollbar sits BELOW the viewport bottom (i.e. currently unreachable).
 *   4. Render a fixed bar at viewport-bottom with an inner spacer sized to
 *      the target's `scrollWidth` — bi-directionally syncs `scrollLeft`.
 *
 * The bar hides when nothing needs it, so short grids don't show a duplicate
 * scrollbar.
 */
import React, { useEffect, useRef, useState, useCallback } from "react";

export default function StickyHorizontalScrollbar() {
  const stickyRef = useRef(null);
  const spacerRef = useRef(null);
  const targetRef = useRef(null);
  const syncingRef = useRef(false);
  const [visible, setVisible] = useState(false);
  const [leftOffset, setLeftOffset] = useState(0);
  const [width, setWidth] = useState(0);

  const findTarget = useCallback(() => {
    const main = document.querySelector("main");
    if (!main) return null;
    const candidates = main.querySelectorAll(".overflow-x-auto, [data-sticky-hscroll]");
    const viewportBottom = window.innerHeight;
    for (const el of candidates) {
      if (el.scrollWidth <= el.clientWidth) continue;  // no horizontal overflow
      const rect = el.getBoundingClientRect();
      // Show the sticky bar whenever the wide grid intersects the viewport —
      // works whether or not its native scrollbar is on-screen. Users get a
      // consistent bottom-of-viewport pan handle either way.
      const visibleInViewport = rect.top < viewportBottom && rect.bottom > 60;
      if (visibleInViewport) {
        return el;
      }
    }
    return null;
  }, []);

  const recompute = useCallback(() => {
    const target = findTarget();
    if (!target) {
      if (visible) setVisible(false);
      targetRef.current = null;
      return;
    }
    targetRef.current = target;
    const rect = target.getBoundingClientRect();
    setLeftOffset(rect.left);
    setWidth(rect.width);
    if (spacerRef.current) {
      spacerRef.current.style.width = `${target.scrollWidth}px`;
    }
    if (stickyRef.current && !syncingRef.current) {
      syncingRef.current = true;
      stickyRef.current.scrollLeft = target.scrollLeft;
      // Release on next frame so target->sticky syncs don't ping-pong.
      requestAnimationFrame(() => { syncingRef.current = false; });
    }
    if (!visible) setVisible(true);
  }, [findTarget, visible]);

  // Sticky bar → target sync.
  const onStickyScroll = () => {
    if (syncingRef.current || !targetRef.current || !stickyRef.current) return;
    syncingRef.current = true;
    targetRef.current.scrollLeft = stickyRef.current.scrollLeft;
    requestAnimationFrame(() => { syncingRef.current = false; });
  };

  // Target → sticky sync (attach listener on the currently-tracked target).
  useEffect(() => {
    const t = targetRef.current;
    if (!t) return;
    const onTargetScroll = () => {
      if (syncingRef.current || !stickyRef.current) return;
      syncingRef.current = true;
      stickyRef.current.scrollLeft = t.scrollLeft;
      requestAnimationFrame(() => { syncingRef.current = false; });
    };
    t.addEventListener("scroll", onTargetScroll, { passive: true });
    return () => t.removeEventListener("scroll", onTargetScroll);
  }, [visible, targetRef.current]);

  // Recompute on any relevant page event.
  useEffect(() => {
    recompute();
    const main = document.querySelector("main");
    const onScroll = () => recompute();
    const onResize = () => recompute();
    window.addEventListener("scroll", onScroll, { passive: true, capture: true });
    window.addEventListener("resize", onResize);
    main?.addEventListener("scroll", onScroll, { passive: true });
    // Watch for DOM changes (route switches, table repaints).
    const mo = new MutationObserver(() => recompute());
    mo.observe(document.body, { childList: true, subtree: true, attributes: false });
    // First-paint safety poll.
    const t = setInterval(recompute, 800);
    return () => {
      window.removeEventListener("scroll", onScroll, { capture: true });
      window.removeEventListener("resize", onResize);
      main?.removeEventListener("scroll", onScroll);
      mo.disconnect();
      clearInterval(t);
    };
  }, [recompute]);

  return (
    <div
      ref={stickyRef}
      data-testid="sticky-h-scrollbar"
      onScroll={onStickyScroll}
      // Feb 2026 · Fix: on macOS + touch-trackpad browsers the OS overlay
      // scrollbar only paints on hover, and squeezing everything into a
      // 14-px tall track left almost no physical pixels for the thumb —
      // users reported "I can't grab it". Bumped height to 18px, forced
      // webkit / Firefox scrollbars to always render, and raised the
      // z-index so the sidebar's border row can't shadow the click zone.
      className="sticky-hscroll-bar"
      style={{
        position: "fixed",
        bottom: 0,
        left: leftOffset,
        width: width,
        height: 18,
        overflowX: "scroll",
        overflowY: "hidden",
        background: "rgba(0,0,0,0.6)",
        zIndex: 60,
        display: visible ? "block" : "none",
        borderTop: "1px solid var(--border, #262626)",
        pointerEvents: "auto",
        // Firefox: force a permanently-visible thin scrollbar
        scrollbarWidth: "auto",
        scrollbarColor: "rgba(180,180,180,0.85) rgba(0,0,0,0.35)",
      }}
    >
      <div ref={spacerRef} style={{ height: 1, width: 1 }} />
      {/* Force WebKit / Blink to draw a chunky visible thumb so users can
          click-and-drag the bar on macOS + Chrome/Safari where the default
          overlay scrollbar is invisible until hover. */}
      <style>{`
        .sticky-hscroll-bar::-webkit-scrollbar { height: 18px; -webkit-appearance: none; }
        .sticky-hscroll-bar::-webkit-scrollbar-track { background: rgba(0,0,0,0.35); }
        .sticky-hscroll-bar::-webkit-scrollbar-thumb { background: rgba(180,180,180,0.85); border-radius: 6px; border: 3px solid rgba(0,0,0,0.35); }
        .sticky-hscroll-bar::-webkit-scrollbar-thumb:hover { background: rgba(220,220,220,0.95); }
      `}</style>
    </div>
  );
}
